import React, { useState, useEffect } from "react";
import Table from "rc-app/components/Table";
import { useNavigate } from "react-router-dom";
import Icon from "rc-app/components/Icons";
import { toTitleCase } from "rc-app/utils";
import { patchProjectDisplayOrder } from "rc-app/api/projectActionsApi";
import UserService from "rc-app/services/UserService";

// Sort by display_order ASC, then created_at DESC as tiebreaker (unset
// orders fall to the bottom). Mirrors the backend's intended sort so the
// rearrange UI starts from the same view the curator sees.
const sortByDisplayOrder = (arr: any[]) =>
  [...arr].sort((a, b) => {
    const aOrder = a?.display_order ?? Number.POSITIVE_INFINITY;
    const bOrder = b?.display_order ?? Number.POSITIVE_INFINITY;
    if (aOrder !== bOrder) return aOrder - bOrder;
    const aTime = new Date(a?.created_at || 0).getTime();
    const bTime = new Date(b?.created_at || 0).getTime();
    return bTime - aTime;
  });

const ProjectsTable = ({
  projects,
  className = "",
  loading = false,
  onProjectsReordered,
}: {
  projects: any[];
  className?: string;
  loading?: boolean;
  onProjectsReordered?: (next: any[]) => void;
}) => {
  const navigate = useNavigate();
  const canRearrange = UserService.hasRole("super_admin");

  // Drag-and-drop state. localOrder is a working copy edited during
  // rearrange mode; on save it's flushed up via onProjectsReordered.
  const [isRearranging, setIsRearranging] = useState(false);
  const [localOrder, setLocalOrder] = useState<any[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  // When entering rearrange mode (or when the upstream projects change
  // mid-mode — e.g. after save refresh), reset the working copy.
  useEffect(() => {
    if (isRearranging) setLocalOrder(sortByDisplayOrder(projects || []));
  }, [isRearranging, projects]);

  const columns = [
    "Project Name",
    "Project Type",
    "Created Date",
    "Order",
    "Edit",
  ];

  const formatDate = (dateString: string) => {
    if (!dateString) return "Not Available";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Not Available";
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  // Normal (non-rearrange) row — read-only Order, Edit button.
  const renderRow = (project: any, index: number) => (
    <tr
      key={index}
      className="hover:bg-[#35495E] transition-colors text-[#F4F5F7] text-base border border-[#374B5E]"
    >
      <td className="px-4 py-3 font-normal">
        {toTitleCase(project.project_name) || "Not Available"}
      </td>
      <td className="px-4 py-3 font-normal">
        {toTitleCase(project.project_type) || "Not Available"}
      </td>
      <td className="px-4 py-3 font-normal">
        {formatDate(project.created_at)}
      </td>
      <td className="px-4 py-3 font-normal">
        {project?.display_order ?? (
          <span className="text-[#889DB2]">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        <button
          type="button"
          onClick={() => navigate(`/edit_project/${project?._id}`)}
          className="px-4 py-2 flex items-center justify-center gap-2 border border-[#008564] bg-[#008564] text-[#fff] text-sm rounded-lg hover:opacity-90 transition-colors font-normal"
          style={{ boxShadow: "0 2px 2px 0 rgba(0, 0, 0, 0.25)" }}
        >
          <Icon name="ic-edit" className="w-4 h-4" />
          Edit
        </button>
      </td>
    </tr>
  );

  // Drag-and-drop handlers for rearrange mode. Pure index swap — we don't
  // re-sort or revalidate; the visible row order IS the source of truth
  // until the user clicks Save.
  const onDragStart = (index: number) => () => setDraggedIndex(index);
  const onDragOver = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    setOverIndex(index);
  };
  const onDrop = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) {
      setDraggedIndex(null);
      setOverIndex(null);
      return;
    }
    setLocalOrder((prev) => {
      const next = [...prev];
      const [moved] = next.splice(draggedIndex, 1);
      next.splice(index, 0, moved);
      return next;
    });
    setDraggedIndex(null);
    setOverIndex(null);
  };
  const onDragEnd = () => {
    setDraggedIndex(null);
    setOverIndex(null);
  };

  const handleCancelRearrange = () => {
    setIsRearranging(false);
    setDraggedIndex(null);
    setOverIndex(null);
  };

  const handleSaveOrder = async () => {
    if (localOrder.length === 0) {
      setIsRearranging(false);
      return;
    }
    setSaving(true);
    try {
      // N parallel per-project PUTs setting display_order = (index+1)*1000.
      // The bulk endpoint /agencies/{id}/projects/order exists per spec
      // but isn't reliably persisting — sticking with per-project PUTs
      // which are confirmed working.
      await Promise.all(
        localOrder.map((p, i) =>
          patchProjectDisplayOrder(p._id, (i + 1) * 1000),
        ),
      );
      const repriced = localOrder.map((p, i) => ({
        ...p,
        display_order: (i + 1) * 1000,
      }));
      if (onProjectsReordered) onProjectsReordered(repriced);
      setIsRearranging(false);
    } catch (err: any) {
      console.error("Reorder failed:", err);
      const status = err?.response?.status;
      if (status === 403) {
        alert("You don't have permission to reorder projects.");
      } else {
        alert(
          "Failed to save order. Some projects may have been reordered; refresh to see the actual state.",
        );
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 bg-[#2E4051] rounded flex items-center justify-center gap-3 text-white">
        <div className="w-5 h-5 border-2 border-[#008564] border-t-transparent rounded-full animate-spin"></div>
        <span>Loading projects...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Action bar — Rearrange Projects button (or Save/Cancel in rearrange mode) */}
      {canRearrange && (
        <div className="flex justify-end mb-3 gap-2">
          {!isRearranging ? (
            <button
              type="button"
              onClick={() => setIsRearranging(true)}
              className="px-4 py-2 flex items-center gap-2 border border-[#374B5E] bg-[#2E4051] text-[#F4F5F7] text-sm rounded-lg hover:bg-[#35495E] transition-colors"
              title="Drag projects up or down to set the order shown to ABA/ASA/agents/customers."
            >
              <Icon name="ic-edit" className="w-4 h-4" />
              Rearrange Projects
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={handleCancelRearrange}
                disabled={saving}
                className="px-4 py-2 border border-[#374B5E] bg-transparent text-[#F4F5F7] text-sm rounded-lg hover:bg-[#35495E] transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveOrder}
                disabled={saving}
                className="px-4 py-2 flex items-center gap-2 border border-[#008564] bg-[#008564] text-white text-sm rounded-lg hover:opacity-90 transition-colors disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save Order"}
              </button>
            </>
          )}
        </div>
      )}

      {!isRearranging ? (
        <Table
          title="Projects"
          columns={columns}
          data={projects}
          renderRow={renderRow}
          emptyMessage="No projects available"
          className={className}
          sortableColumns={[0, 1, 2, 3]}
          columnKeys={[
            "project_name",
            "project_type",
            "created_at",
            "display_order",
          ]}
        />
      ) : (
        // Rearrange mode: custom draggable table. Sort/Edit are hidden;
        // the only action is drag-and-drop. A leading drag-handle column
        // signals draggability; the whole row is also draggable.
        <div className="rounded-lg border border-[#374B5E]">
          <table className="min-w-full border-collapse rounded-lg">
            <thead className="sticky top-0 z-10">
              <tr className="bg-[#222F3C] text-[#889DB2] border-b-2 border-[#374B5E]">
                <th className="px-2 py-3 text-base font-normal w-8"></th>
                <th className="px-4 py-3 text-base font-normal text-left">
                  Project Name
                </th>
                <th className="px-4 py-3 text-base font-normal text-left">
                  Project Type
                </th>
                <th className="px-4 py-3 text-base font-normal text-left">
                  Created Date
                </th>
                <th className="px-4 py-3 text-base font-normal text-left">
                  New Order
                </th>
              </tr>
            </thead>
            <tbody>
              {localOrder.map((project, index) => {
                const isDragging = draggedIndex === index;
                const isDragOver = overIndex === index;
                return (
                  <tr
                    key={project._id || index}
                    draggable
                    onDragStart={onDragStart(index)}
                    onDragOver={onDragOver(index)}
                    onDrop={onDrop(index)}
                    onDragEnd={onDragEnd}
                    className={`transition-colors text-[#F4F5F7] text-base border border-[#374B5E] cursor-grab active:cursor-grabbing ${
                      isDragging ? "opacity-40" : ""
                    } ${
                      isDragOver
                        ? "bg-[#1f3f4f] border-t-2 border-t-[#008564]"
                        : "hover:bg-[#35495E]"
                    }`}
                  >
                    <td className="px-2 py-3 text-[#889DB2] text-center select-none">
                      ⋮⋮
                    </td>
                    <td className="px-4 py-3 font-normal">
                      {toTitleCase(project.project_name) || "Not Available"}
                    </td>
                    <td className="px-4 py-3 font-normal">
                      {toTitleCase(project.project_type) || "Not Available"}
                    </td>
                    <td className="px-4 py-3 font-normal">
                      {formatDate(project.created_at)}
                    </td>
                    <td className="px-4 py-3 font-normal text-[#889DB2]">
                      {(index + 1) * 1000}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="mt-3 text-sm text-[#889DB2]">
            Drag rows up or down to reorder. The "New Order" column previews
            what each project's order will be after you click Save.
          </p>
        </div>
      )}
    </div>
  );
};

export default ProjectsTable;
